'use client';

import { useState } from 'react';
import posthog from 'posthog-js';
import type {
  QuizLanguage,
  QuizSource,
  SwapQuizQuestionBody,
} from '@peach/types/quizzes/generated-quiz.dto.js';
import { swapQuizQuestion } from '@/app/actions/quiz-gen';
import type { EditableQuiz } from './QuizFields';

/**
 * Writes one question again on the quiz's own source, never the whole quiz. No `brief`
 * (a quiz stored before it was kept) means no swap: `canSwap` is false.
 */
export function useQuestionSwap<T extends EditableQuiz>(
  quiz: T,
  source: QuizSource & { language?: QuizLanguage },
  onChange: (next: T) => void
) {
  const { brief, audience, taught, voice, language } = source;
  const [busy, setBusy] = useState<number | null>(null);
  const [failed, setFailed] = useState<number | null>(null);

  const swap = async (index: number) => {
    if (!brief) return;
    setBusy(index);
    setFailed(null);
    const body: SwapQuizQuestionBody = {
      what: brief,
      audience,
      language,
      taught,
      voice,
      title: quiz.title,
      questions: quiz.questions.map((q) => q.text),
      index,
    };
    const result = await swapQuizQuestion(body);
    setBusy(null);
    posthog.capture('ai_draft_question_swapped', { index, ok: result.ok });
    if (!result.ok) {
      setFailed(index);
      return;
    }
    // The written question lands on the old one's shape, so an id it carries survives.
    const old = quiz.questions[index]!;
    onChange({
      ...quiz,
      questions: quiz.questions.map((q, i) =>
        i === index
          ? {
              ...old,
              text: result.question.text,
              options: result.question.options.map((option, k) => ({
                ...old.options[k]!,
                ...option,
              })),
            }
          : q
      ),
    });
  };

  return { canSwap: !!brief, swap, busy, failed };
}
